export const getUserBrowser = (): string => {
  const userAgent = navigator.userAgent;
  let browserType;
  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserType = 'chrome';
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserType = 'firefox';
  } else if (userAgent.match(/safari/i)) {
    browserType = 'safari';
  } else if (userAgent.match(/edg/i)) {
    browserType = 'edge';
  } else {
    browserType = 'UNKNOWN';
  }

  return browserType;
};

export const isEdgeOnIOSOrAndroid = (): boolean => {
  const ua = navigator.userAgent || navigator.vendor || '';
  const isEdge = /Edg|EdgiOS|EdgA/i.test(ua);
  const isIOS =
    /iPad|iPhone|iPod/i.test(ua) || /Macintosh;.*CPU iPhone OS/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  return isEdge && (isIOS || isAndroid);
};

export const showInitMessage = (message: string) => {
  const parsedMessage: {
    initMessage1?: string;
    initMessage2?: string;
    initMessage3?: string;
  } = {};

  if (message.length < 1024) {
    parsedMessage['initMessage1'] = message;
  } else if (message.length > 1024 && message.length < 2048) {
    parsedMessage['initMessage1'] = message.slice(0, 1024);
    parsedMessage['initMessage2'] = message.slice(1024, 2048);
  } else {
    parsedMessage['initMessage1'] = message.slice(0, 1024);
    parsedMessage['initMessage2'] = message.slice(1024, 2048);
    parsedMessage['initMessage3'] = message.slice(2048, 3072);
  }

  console.log({
    initMessage: JSON.parse(message),
    parsedMessage,
    stringifiedMessage: message,
  });
};

// AWS fires the session-length cutoff a beat after the deadline (~2s observed on
// a 60s cap), so a disconnect this close to it is the configured cutoff rather
// than a crash.
export const GPU_SESSION_CAP_TOLERANCE_MS = 5_000;

/**
 * Reports whether a stream-session termination was the project's configured
 * max GPU session duration rather than an unexpected failure.
 */
export const isGpuSessionCapReached = (
  deadline: number | null,
  now: number = Date.now(),
): boolean =>
  deadline !== null && now >= deadline - GPU_SESSION_CAP_TOLERANCE_MS;

/**
 * Resolves when AWS will terminate a session, from the cap it reports on the
 * session becoming ACTIVE. Returns null when the project configures no cap.
 */
export const resolveGpuSessionDeadline = (
  sessionLengthSeconds: number | undefined,
  now: number = Date.now(),
): number | null =>
  typeof sessionLengthSeconds === 'number' && sessionLengthSeconds > 0
    ? now + sessionLengthSeconds * 1000
    : null;

// Warnings shown before the cutoff, latest lead first. Each has its own string
// rather than an interpolated minute count: "in 1 minutes" is wrong, and correct
// plural forms would mean up to six variants per language across 23 locales.
export const GPU_SESSION_WARNINGS = [
  {
    leadMs: 5 * 60 * 1000,
    messageKey: 'notifications.gpuSessionDurationWarning5Min',
  },
  {
    leadMs: 60 * 1000,
    messageKey: 'notifications.gpuSessionDurationWarning1Min',
  },
] as const;

export interface IGpuSessionWarning {
  delayMs: number;
  messageKey: string;
}

/**
 * Resolves which end-of-session warnings to schedule for a given cap.
 *
 * A warning is skipped when its lead is more than half the session, so a short
 * cap cannot spend most of its life announcing that it is about to end — a
 * 1-minute cap warns not at all, and never claims "5 minutes" at t=0.
 */
export const resolveGpuSessionWarnings = (
  sessionLengthSeconds: number | undefined,
): IGpuSessionWarning[] => {
  if (typeof sessionLengthSeconds !== 'number' || sessionLengthSeconds <= 0) {
    return [];
  }

  const sessionLengthMs = sessionLengthSeconds * 1000;

  return GPU_SESSION_WARNINGS.filter(
    ({ leadMs }) => sessionLengthMs > leadMs * 2,
  ).map(({ leadMs, messageKey }) => ({
    delayMs: sessionLengthMs - leadMs,
    messageKey,
  }));
};
